import { useMeasure } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { Container } from '@sorare/core/src/atoms/layout/Container';
import { getBackground } from '@sorare/core/src/atoms/ui/Background/utils';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

const StyledContainer = styled(Container)`
  --container-padding: 0px;
  @media ${laptopAndAbove} {
    --container-padding: var(--double-unit);
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: var(--triple-unit);
  @media ${laptopAndAbove} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 1fr;
  }
`;
const Sticky = styled.div`
  position: sticky;
  z-index: 1;
  top: 0;
`;
const Header = styled.div`
  padding: var(--unit) var(--double-unit);
  @media ${laptopAndAbove} {
    padding: var(--unit) 0;
  }
`;
const scaleDown = keyframes`
  to {
    transform: scale(0.8);
  }
`;

const CardContainer = styled.div<{ headerHeight: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  width: 580px;
  margin: 0 auto;
  animation: ${scaleDown} auto linear;
  animation-timeline: scroll();
  @media screen and (min-height: 450px) {
    position: sticky;
    height: ${({ headerHeight }) =>
      `calc(var(--100vh) - ${headerHeight}px - 80px)`}; /** substracting the amount of visible px of the next block */
    top: ${({ headerHeight }) => `${headerHeight}px`};
  }
  @media ${laptopAndAbove} {
    animation: none;
    justify-content: flex-start;
    height: ${({ headerHeight }) =>
      `calc(var(--100vh) - ${headerHeight}px - var(--double-unit))`}; /** bottom margin */
  }
`;

const Content = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--triple-unit);
  color: var(--c-white);
  background: var(--c-nl-500);
  backdrop-filter: blur(4px);
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    background: none;
    backdrop-filter: none;
    min-width: 0;
  }
`;

type Props = {
  header: React.ReactNode;
  preview: React.ReactNode;
  children?: React.ReactNode;
  backgroundName?: string;
};

export const CardPageLayout = ({
  header,
  preview,
  children,
  backgroundName,
}: Props) => {
  const [measurementDiv, { height }] = useMeasure<HTMLDivElement>();
  return (
    <>
      <StyledContainer style={getBackground(backgroundName)}>
        <Sticky ref={measurementDiv}>
          <Header>{header}</Header>
        </Sticky>
        <InnerContainer>
          <CardContainer headerHeight={height}>{preview}</CardContainer>
          <Content>{children}</Content>
        </InnerContainer>
      </StyledContainer>
    </>
  );
};
